import { computed, type ComputedRef, type Ref } from 'vue';
import type { Options, Point, TooltipFormatterContextObject } from 'highcharts';
import merge from 'lodash-es/merge';
import useColumnChartOptions from '@/components/charts/composables/useColumnChartOptions';
import { withAlpha } from '@/plugins/highcharts/chartColors';
import { i18n } from '@/plugins/internationalization/i18n';
import { toDateString } from './useRecordsAggregation';
import { CHART_HEIGHT, UNSELECTED_OPACITY, clearHoverBand, setHoverBand, weekTooltipHeader } from './useRecordsChartOptions';
import type { GroupedWeeklySeries } from './useGroupedWeeklyAggregation';

export type GroupedWeeklyChartConfig = {
    /**
     * The colours the plotted series take, in the order they are plotted - so the series highest in
     * the ranking take the first entries. Cycles once more series are plotted than colours exist.
     */
    colors: string[];
    /** What the bars count, in the page's own words. Translated by the caller. */
    title: Ref<string>;
    /** Prefix of every series' Highcharts id, naming what a series is (`feed-mix`, `drug`). See the id below. */
    seriesIdPrefix: string;
    /** More about one series' week, put after its value in the tooltip; nothing when absent or null. Translated by the caller. */
    pointDetail?: (weekStart: string, seriesId: number) => string | null;
    onBinClicked: (weekStart: string) => void;
};

/**
 * The weekly grouped column chart a page navigates its tables with: one bar per series per week,
 * side by side rather than stacked - so no `stacking` option is set, which is how Highcharts groups
 * multiple column series by default. Click a bar to select its week, click again to clear, same as
 * `useRecordsChartOptions`, whose selection fade and hover band this shares. The legend is
 * read-only: the page's series dropdown is the one control over which series are drawn, so a
 * legend click is refused rather than left as a second way to hide one.
 */
export function useGroupedWeeklyChartOptions(series: Ref<GroupedWeeklySeries[]>, selectedWeekStart: Ref<string | null>, config: GroupedWeeklyChartConfig): ComputedRef<Options> {
    /*
     * Static defaults only, taken once - see `useRecordsChartOptions` for why a `computed` cannot be
     * handed to the shared composable. The week-dependent half is merged on top per render below.
     */
    const columnDefaults = useColumnChartOptions({});

    /** The colour of the series in the given plot position. */
    function seriesColor(index: number): string {
        return config.colors[index % config.colors.length];
    }

    const highchartsSeries = computed(() =>
        series.value.map((entry, index) => {
            const color = seriesColor(index);

            return {
                type: 'column',
                // Every Records chart plots exactly one series, so `GenericChart`'s zoom modal -
                // which derives each series' modal-clone id as `${s.id}-generic-chart-modal` -
                // never needed a real id to stay unique: `undefined-generic-chart-modal` is still
                // one-of-one there. This chart plots several series, so without a distinct id here
                // every clone collides on that same string and Highcharts' id-based series diffing
                // collapses them all away - the modal opens with zero series and no axes, nothing
                // thrown. Keyed by the series id, which is stable across a data refresh.
                id: `${config.seriesIdPrefix}-${entry.id}`,
                name: entry.name,
                color,
                data: entry.data.map((point) => ({
                    x: point.x,
                    y: point.y,
                    color: selectedWeekStart.value !== null && selectedWeekStart.value !== toDateString(point.x) ? withAlpha(color, UNSELECTED_OPACITY) : color,
                })),
            };
        })
    );

    return computed(() => {
        const base = merge({}, columnDefaults.value) as Options;

        return merge(base, {
            chart: {
                height: CHART_HEIGHT,
                // `useDefaultChartOptions` fixes `marginRight` at 48px, which is enough for the
                // shorter counts every Records chart plots but clips this one's y-axis labels
                // (opposite: true, so they hang off the right) once weekly values reach 6 digits,
                // as delivered feed does. `null` restores Highcharts' own default of sizing the
                // margin to whatever the rendered labels actually need.
                marginRight: null,
            },
            title: {
                text: config.title.value,
            },
            xAxis: {
                min: null,
                max: null,
            },
            yAxis: {
                min: 0,
                allowDecimals: false,
            },
            legend: {
                enabled: true,
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    borderWidth: 0,
                    borderRadius: 2,
                    pointPadding: 0.05,
                    groupPadding: 0.12,
                },
                series: {
                    // Highcharts dims every other series member while one is hovered, which fights
                    // the fade that marks a bar as unselected.
                    states: { inactive: { opacity: 1 } },
                    // The dropdown owns which series are visible; a legend click toggling a series
                    // on top of that would let the two disagree about what is shown.
                    events: {
                        legendItemClick() {
                            return false;
                        },
                    },
                    point: {
                        events: {
                            click() {
                                config.onBinClicked(toDateString((this as unknown as Point).x));
                            },
                            mouseOver() {
                                const point = this as unknown as Point;

                                // The band takes the hovered bar's own series colour, read back from
                                // the plot position rather than off the point, whose colour is the
                                // faded variant whenever another week is selected.
                                setHoverBand(point.series?.chart, point.x, seriesColor(point.series?.index ?? 0));
                            },
                            mouseOut() {
                                const point = this as unknown as Point;

                                clearHoverBand(point.series?.chart);
                            },
                        },
                    },
                },
            },
            tooltip: {
                // One entry per series that week, under a single week header - the bars of a week
                // are read against each other, not one at a time.
                shared: true,
                formatter(this: TooltipFormatterContextObject) {
                    const points = this.points ?? [];

                    if (!points.length) {
                        return '';
                    }

                    const weekStart = toDateString(points[0].x as number);
                    const lines = points
                        .map((point) => {
                            const seriesId = series.value[point.series.index]?.id;
                            const detail = seriesId === undefined ? null : config.pointDetail?.(weekStart, seriesId) ?? null;

                            return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n((point.y as number) ?? 0, 'decimal')}</b>${detail ? ` · ${detail}` : ''}<br/>`;
                        })
                        .join('');

                    return weekTooltipHeader(points[0].x as number) + lines;
                },
            },
            series: highchartsSeries.value,
        });
    });
}
